import { Dispatch, FC, ReactNode, SetStateAction, createContext, useContext, useEffect, useMemo, useState } from 'react'
import { ProductKind, ProductKindsWithDocumentGallery, ProductType } from 'constants/product'
import { bigFromDecimal, percentageValue, valueAfterDiscount } from 'utils/price'

import { AdditionalVisualPriceDTO } from 'models/visuals'
import { AxiosResponse } from 'axios'
import Big from 'big.js'
import { Currency } from 'constants/misc'
import { PIPEDRIVE_INFINITY } from 'constants/pipedrive'
import { ProductCategory } from 'models/product'
import { deriveProductTypeBucket } from 'utils/product'
import { isEditingCategory } from 'utils/validators'
import { useAuth0 } from 'utils/auth'
import { useGalleryAssignment } from './GalleryAssignment.context'
import { useGalleryConstants } from './GalleryConstants.context'
import { useGalleryProduct } from './GalleryProduct.context'
import { useGalleryVisualSelection } from './GalleryVisualSelection.context'
import { useGalleryVisualType } from './GalleryVisualType.context'
import { useGalleryVisualsMeta } from './GalleryVisualsMeta.context'
import { useGetAdditionalVisualPrice } from 'dataQueries'
import { useUserData } from 'components/contexts/UserDataContext'

const disabledVisualsProductKinds = new Set([
  ProductKind.COMMERCIAL_STAGING,
  ProductKind.RESIDENTIAL_STAGING,
])

const additionalPurchaseAllowedEditingTypes = new Set([
  ProductType.PHOTO,
  ProductType.AI_PHOTO_EDITING
])

interface GalleryAdditionalVisualsInterface {
  getAdditionalVisualPriceResponse?: AxiosResponse<AdditionalVisualPriceDTO, any>
  currency?: Currency
  pricePerAdditionalVisual: Big
  vat: Big
  discount: Big
  selectedAdditionalVisualsCount: number
  selectedFreeVisualsCount: number
  selectedPaidVisualsCount: number
  totalForAdditionalVisualsBeforeDiscount: Big
  totalForAdditionalVisualsAfterDiscount: Big
  totalVatForAdditionalVisuals: Big
  totalAfterVatForAdditionalVisuals: Big
  areAdditionalVisualsSelected: boolean
  isAdditionalVisualPurchaseDisabled: boolean
  additionalVisualProductId?: number
  isAdditionalVisualsPurchaseModalOpen: boolean
  setIsAdditionalVisualsPurchaseModalOpen: Dispatch<SetStateAction<boolean>>
}

const defaultGalleryAdditionalVisualsValue: GalleryAdditionalVisualsInterface = {
  pricePerAdditionalVisual: new Big(0),
  vat: new Big(0),
  discount: new Big(0),
  selectedAdditionalVisualsCount: 0,
  totalForAdditionalVisualsBeforeDiscount: new Big(0),
  totalForAdditionalVisualsAfterDiscount: new Big(0),
  totalVatForAdditionalVisuals: new Big(0),
  totalAfterVatForAdditionalVisuals: new Big(0),
  areAdditionalVisualsSelected: false,
  isAdditionalVisualPurchaseDisabled: false,
  additionalVisualProductId: undefined,
  isAdditionalVisualsPurchaseModalOpen: false,
  setIsAdditionalVisualsPurchaseModalOpen: () => { },
  selectedFreeVisualsCount: 0,
  selectedPaidVisualsCount: 0,
}

/** Gallery additional visuals context */
export const GalleryAdditionalVisualsContext = createContext<GalleryAdditionalVisualsInterface>(defaultGalleryAdditionalVisualsValue)
/** Gallery additional visuals context hook */
export const useGalleryAdditionalVisuals = (): GalleryAdditionalVisualsInterface => useContext(GalleryAdditionalVisualsContext)

/** Context provider for gallery additional visuals */
export const GalleryAdditionalVisualsContextProvider: FC<{
  assignmentId: string
  children?: ReactNode
}> = ({
  assignmentId,
  children,
}) => {
    const { roles } = useAuth0()

    const { userPropertyWorkspaceId } = useUserData()
    const { VisualTypeSelectEnum } = useGalleryConstants()
    const { isVirtualVisit } = useGalleryProduct()
    const { visualTypeSelect } = useGalleryVisualType()

    const {
      assignmentData,
      product,
    } = useGalleryAssignment()

    const {
      purchasedVisualsExist,
      prepurchasedVisualsTotal,
      prepurchasedVisualsRemaining,
    } = useGalleryVisualsMeta()

    const {
      canSelectVisuals,
      selectedNotPurchasedVisualsCount,
      selectedPhotosCount,
    } = useGalleryVisualSelection()

    const [isAdditionalVisualsPurchaseModalOpen, setIsAdditionalVisualsPurchaseModalOpen] = useState(false)

    const isAdditionalVisualPurchaseDisabled = useMemo(() => {
      if (!assignmentData) return true
      if (!product) return true

      // This gate leads to a real charge, so it fails closed. The classification flags are absent
      // until the ISSUE-2539 backend release lands; without this check they read as undefined,
      // every branch below is falsy, and purchase would be *enabled* for the very assignment types
      // the business blocks. isStaging360 is sentinel-checked too — the STAGING_360 exclusion below
      // depends on it (ISSUE-2745). Remove once that backend release is in every environment.
      if (product.isFloorPlan === undefined || product.isStaging360 === undefined) return true

      // Document deliverables have no visuals to buy; decided by kind so the flags cannot re-enable it.
      if (ProductKindsWithDocumentGallery.has(product.kind)) return true

      if (product.isFloorPlan || product.isVirtualVisit || product.isVideo) return true

      // Allows additional purchases for some editing category products. Bucket-derived instead of
      // the deprecated wire `type` (ISSUE-2745). PROD census 2026-07-31: the editing category holds
      // types PHOTO / AI (allowed) and VIDEO / FLOOR_PLAN / STAGING_360 (blocked); the first four map
      // to the bucket 1:1, STAGING_360 falls through to the PHOTO bucket and is excluded via its
      // flag. Plain staging kinds carry type=PHOTO and stay allowed, as with the legacy type read.
      if (isEditingCategory(product.category as ProductCategory)) {
        if (product.isStaging360) return true
        return !additionalPurchaseAllowedEditingTypes.has(deriveProductTypeBucket(product))
      }

      if (disabledVisualsProductKinds.has(product.kind)) return true

      return false
    }, [assignmentData, product])

    // Contextual eligibility for the additional-visual price fetch: only contexts that
    // genuinely support the additional purchase flow (not virtual visits, not
    // purchase-disabled products). Role authorization for the endpoint is enforced inside
    // useGetAdditionalVisualPrice, so it is intentionally not duplicated here.
    const canFetchAdditionalVisualPrice = !isVirtualVisit && !isAdditionalVisualPurchaseDisabled

    // React query
    const getAdditionalVisualPrice = useGetAdditionalVisualPrice(assignmentId, userPropertyWorkspaceId, roles, canFetchAdditionalVisualPrice)

    const getAdditionalVisualPriceResponse = getAdditionalVisualPrice.data

    const currency = useMemo(() => getAdditionalVisualPriceResponse?.data?.fee.currency, [getAdditionalVisualPriceResponse])
    const pricePerAdditionalVisual = useMemo(() => bigFromDecimal(getAdditionalVisualPriceResponse?.data?.fee.value || 0), [getAdditionalVisualPriceResponse])
    const vat = useMemo(() => bigFromDecimal(getAdditionalVisualPriceResponse?.data?.vat.value || 0), [getAdditionalVisualPriceResponse])
    const discount = useMemo(() => bigFromDecimal(getAdditionalVisualPriceResponse?.data?.discount.value || 0), [getAdditionalVisualPriceResponse])
    const additionalVisualProductId = useMemo(() => getAdditionalVisualPriceResponse?.data?.productId, [getAdditionalVisualPriceResponse])

    const selectedAdditionalVisualsCount = useMemo(() => {
      if (isAdditionalVisualPurchaseDisabled) return 0

      if (!purchasedVisualsExist) {
        if (selectedPhotosCount > prepurchasedVisualsTotal) return (selectedPhotosCount - prepurchasedVisualsTotal)
        return 0
      }

      if (prepurchasedVisualsRemaining > 0) {
        if (selectedNotPurchasedVisualsCount > prepurchasedVisualsRemaining) return (selectedNotPurchasedVisualsCount - prepurchasedVisualsRemaining)
        return 0
      }

      return selectedNotPurchasedVisualsCount
    }, [isAdditionalVisualPurchaseDisabled, purchasedVisualsExist, prepurchasedVisualsRemaining, selectedNotPurchasedVisualsCount, selectedPhotosCount, prepurchasedVisualsTotal])

    const selectedPaidVisualsCount = useMemo(() => {
      const paidCount = Math.max(selectedNotPurchasedVisualsCount - prepurchasedVisualsRemaining, 0)

      if (paidCount > 0 && isAdditionalVisualPurchaseDisabled) return 0

      return paidCount
    }, [isAdditionalVisualPurchaseDisabled, prepurchasedVisualsRemaining, selectedNotPurchasedVisualsCount])

    const selectedFreeVisualsCount = useMemo(() => Math.min(selectedNotPurchasedVisualsCount, prepurchasedVisualsRemaining), [prepurchasedVisualsRemaining, selectedNotPurchasedVisualsCount])

    const totalForAdditionalVisualsBeforeDiscount = useMemo(() => pricePerAdditionalVisual.times(selectedAdditionalVisualsCount), [pricePerAdditionalVisual, selectedAdditionalVisualsCount])
    const totalForAdditionalVisualsAfterDiscount = useMemo(() => valueAfterDiscount(totalForAdditionalVisualsBeforeDiscount, discount), [totalForAdditionalVisualsBeforeDiscount, discount])
    const totalVatForAdditionalVisuals = useMemo(() => percentageValue(totalForAdditionalVisualsAfterDiscount, vat), [totalForAdditionalVisualsAfterDiscount, vat])
    const totalAfterVatForAdditionalVisuals = useMemo(() => totalForAdditionalVisualsAfterDiscount.plus(totalVatForAdditionalVisuals), [totalForAdditionalVisualsAfterDiscount, totalVatForAdditionalVisuals])
    const areAdditionalVisualsSelected = useMemo(
      () => !isVirtualVisit && canSelectVisuals && (roles.isClient || roles.isAdmin) && (visualTypeSelect === VisualTypeSelectEnum.POST || visualTypeSelect === VisualTypeSelectEnum.POST_WATERMARK) && !isAdditionalVisualPurchaseDisabled && selectedAdditionalVisualsCount > 0 && prepurchasedVisualsTotal !== PIPEDRIVE_INFINITY && !getAdditionalVisualPriceResponse,
      [isVirtualVisit, canSelectVisuals, roles.isClient, roles.isAdmin, visualTypeSelect, VisualTypeSelectEnum.POST, VisualTypeSelectEnum.POST_WATERMARK, isAdditionalVisualPurchaseDisabled, selectedAdditionalVisualsCount, prepurchasedVisualsTotal, getAdditionalVisualPriceResponse]
    )

    // Fetch additional visual price action when selected size exceeds the bought value
    useEffect(() => {
      if (areAdditionalVisualsSelected) {
        getAdditionalVisualPrice.refetch()
      }
    }, [areAdditionalVisualsSelected, assignmentId, getAdditionalVisualPrice])

    return (
      <GalleryAdditionalVisualsContext.Provider
        value={{
          getAdditionalVisualPriceResponse,
          currency,
          pricePerAdditionalVisual,
          vat,
          discount,
          selectedAdditionalVisualsCount,
          totalForAdditionalVisualsBeforeDiscount,
          totalForAdditionalVisualsAfterDiscount,
          totalVatForAdditionalVisuals,
          totalAfterVatForAdditionalVisuals,
          areAdditionalVisualsSelected,
          isAdditionalVisualPurchaseDisabled,
          additionalVisualProductId,
          isAdditionalVisualsPurchaseModalOpen,
          selectedFreeVisualsCount,
          selectedPaidVisualsCount,
          setIsAdditionalVisualsPurchaseModalOpen,
        }}
      >
        {children}
      </GalleryAdditionalVisualsContext.Provider>
    )
  }
